import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Clock, Loader2,
    Sparkles, Target, Phone, Lock, AlertCircle, Zap,
    Trophy, ArrowRight, Award,
    Star, Briefcase, Quote, BadgeCheck,
    Landmark, Cpu, PieChart, ChevronRight, Check,
    Timer, Info, User, ShieldOff
} from 'lucide-react';
import { submitAssessment, API_BASE } from '../services/api';
import Confetti from 'react-confetti';
import useWindowSize from 'react-use/lib/useWindowSize';

const TOTAL_TIME_SECONDS = 1500; // 25 minutes

// Calculate seconds remaining until end of a given date (midnight)
const getSecondsUntilEndOfDay = (dateStr) => {
    const now = new Date();
    let target;
    if (dateStr) {
        // Use the closure date passed in the URL - count down to 11:59:59 PM of that day
        target = new Date(dateStr + 'T23:59:59');
    } else {
        // Fallback: end of today
        target = new Date();
        target.setHours(23, 59, 59, 0);
    }
    return Math.max(0, Math.floor((target - now) / 1000));
};

export default function ScholarshipPortalSupabase() {
    const [searchParams] = useSearchParams();
    const leadIdFromUrl = searchParams.get('id');
    const studentNameFromUrl = searchParams.get('name');
    const closureDateFromUrl = searchParams.get('closure');
    const durationFromUrl = searchParams.get('duration');

    const [isVerified, setIsVerified] = useState(false); // true after OTP verified
    const [view, setView] = useState('landing');
    const [testQuestions, setTestQuestions] = useState([]);
    const [loadingQuestions, setLoadingQuestions] = useState(false);

    // Portal gate state
    const [portalGateChecked, setPortalGateChecked] = useState(false);
    const [portalBlocked, setPortalBlocked] = useState(false);
    const [blockReason, setBlockReason] = useState('closed'); // 'closed' or 'expired'

    // For Confetti
    const { width, height } = useWindowSize();

    const [phone, setPhone] = useState('');
    const [otp, setOtp] = useState('');
    const [isOtpSent, setIsOtpSent] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [leadId, setLeadId] = useState(null);

    const [answers, setAnswers] = useState({});
    const [currentQ, setCurrentQ] = useState(0);
    const [timeLeft, setTimeLeft] = useState(TOTAL_TIME_SECONDS);
    const [offerTimeLeft, setOfferTimeLeft] = useState(() => getSecondsUntilEndOfDay(closureDateFromUrl));
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [evalStep, setEvalStep] = useState(0);
    const [results, setResults] = useState(null);
    const [resendTimer, setResendTimer] = useState(0);
    const [showPopup, setShowPopup] = useState(false);
    const [headerTimer, setHeaderTimer] = useState(() => getSecondsUntilEndOfDay(closureDateFromUrl));
    const [isCtaClicked, setIsCtaClicked] = useState(false);


    // Personalization from URL
    const [personalization, setPersonalization] = useState({
        name: '',
        closure: '',
        duration: ''
    });

    useEffect(() => {
        setPersonalization({
            name: studentNameFromUrl || '',
            closure: closureDateFromUrl || '',
            duration: durationFromUrl || ''
        });
    }, [studentNameFromUrl, closureDateFromUrl, durationFromUrl]);

    // Header Countdown Timer
    useEffect(() => {
        const timer = setInterval(() => {
            setHeaderTimer(prev => (prev > 0 ? prev - 1 : 0));
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    const formatHeaderTime = (seconds) => {
        if (seconds <= 0) return "EXPIRED";
        const d = Math.floor(seconds / (24 * 3600));
        const h = Math.floor((seconds % (24 * 3600)) / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = seconds % 60;
        return `${d}d ${h}h ${m}m ${s}s`;
    };

    const formatTime = (seconds) => {
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = seconds % 60;
        return `${h}h ${m}m ${s}s`;
    };

    // Show popup after 5 seconds
    useEffect(() => {
        const timer = setTimeout(() => {
            if (view === 'landing') setShowPopup(true);
        }, 5000);
        return () => clearTimeout(timer);
    }, [view]);

    const partners = [
        { name: "IIM Ahmedabad", logo: "https://backend.jaroeducation.com/wp-content/uploads/2025/03/IIMA-1.png" },
        { name: "IIM Kozhikode", logo: "https://backend.jaroeducation.com/wp-content/uploads/2025/03/IIMK.png" },
        { name: "IIM Mumbai", logo: "https://backend.jaroeducation.com/wp-content/uploads/2025/03/IIMM.png" },
        { name: "IIM Indore", logo: "https://jaro-website.s3.ap-south-1.amazonaws.com/wp-content/uploads/2025/04/03121809/IIMI-LOGO-560-x-390.jpg" },
        { name: "IIM Tiruchirappalli", logo: "https://backend.jaroeducation.com/wp-content/uploads/2025/03/IIMT.png" },
        { name: "IIM Nagpur", logo: "https://backend.jaroeducation.com/wp-content/uploads/2025/03/IIMN.png" },
        { name: "IIM Visakhapatnam", logo: "https://backend.jaroeducation.com/wp-content/uploads/2025/03/IIMV.png" },
        { name: "IITM Pravartak", logo: "https://backend.jaroeducation.com/wp-content/uploads/2025/03/IITMP.png" },
        { name: "IIT Delhi", logo: "https://backend.jaroeducation.com/wp-content/uploads/2025/03/IITD.png" },
        { name: "IIT Bombay", logo: "https://jaro-website.s3.ap-south-1.amazonaws.com/wp-content/uploads/2025/05/14212437/IIT-B-560-x-390.png" },
        { name: "IIT Kanpur", logo: "https://jaro-website.s3.ap-south-1.amazonaws.com/wp-content/uploads/2025/10/29114932/5-Kanpur.png" },
        { name: "IIT Roorkee", logo: "https://backend.jaroeducation.com/wp-content/uploads/2025/03/IITR.png" },
        { name: "IIT Guwahati", logo: "https://jaro-website.s3.ap-south-1.amazonaws.com/wp-content/uploads/2025/10/29114929/1-IIT-Guwahti.png" },
        { name: "IIT Jodhpur", logo: "https://backend.jaroeducation.com/wp-content/uploads/2025/03/IITJ.png" },
        { name: "IISc", logo: "https://backend.jaroeducation.com/wp-content/uploads/2025/03/IISC.png" },
        { name: "Manipal (MAHE)", logo: "https://backend.jaroeducation.com/wp-content/uploads/2025/03/MAHE-1.png" },
        { name: "Amrita", logo: "https://backend.jaroeducation.com/wp-content/uploads/2025/04/Amrita-560-x-390.jpg" },
        { name: "XLRI", logo: "https://jaro-website.s3.ap-south-1.amazonaws.com/wp-content/uploads/2025/10/29114930/7-Xlri.png" },
        { name: "Chandigarh University", logo: "https://backend.jaroeducation.com/wp-content/uploads/2025/03/CU.png" },
        { name: "Amity University", logo: "https://backend.jaroeducation.com/wp-content/uploads/2025/03/Amity.png" },
        { name: "Symbiosis", logo: "https://jaro-website.s3.ap-south-1.amazonaws.com/wp-content/uploads/2025/10/29114931/6-SIU.png" },
        { name: "DPU", logo: "https://backend.jaroeducation.com/wp-content/uploads/2025/03/DPU.png" },
        { name: "LIBA", logo: "https://backend.jaroeducation.com/wp-content/uploads/2025/03/LIBA.png" },
        { name: "Manipal University Jaipur", logo: "https://backend.jaroeducation.com/wp-content/uploads/2025/03/MUJ.png" },
        { name: "BVDU", logo: "https://backend.jaroeducation.com/wp-content/uploads/2025/03/BVDU.png" },
        { name: "We School", logo: "https://backend.jaroeducation.com/wp-content/uploads/2025/03/WS.png" },
        { name: "Parul University", logo: "https://backend.jaroeducation.com/wp-content/uploads/2025/03/PU.png" },
        { name: "Sikkim Manipal", logo: "https://backend.jaroeducation.com/wp-content/uploads/2025/03/SMU.png" },
        { name: "Dr. MGR University", logo: "https://backend.jaroeducation.com/wp-content/uploads/2025/03/Dr.-MGR.png" },
        { name: "Dayananda Sagar", logo: "https://backend.jaroeducation.com/wp-content/uploads/2025/03/DSU.png" },
        { name: "Harvard", logo: "https://jaro-website.s3.ap-south-1.amazonaws.com/wp-content/uploads/2025/10/29114933/3-Harvard-logo.png" },
        { name: "IDSS", logo: "https://jaro-website.s3.ap-south-1.amazonaws.com/wp-content/uploads/2025/10/29114934/2-idss.png" },
        { name: "Rotman", logo: "https://backend.jaroeducation.com/wp-content/uploads/2025/03/ROTMAN.png" }
    ];


    const loadPortalData = async () => {
        setLoadingQuestions(true);
        try {
            const qRes = await fetch(`${API_BASE}/assessment/questions`);
            const qData = await qRes.json();
            if (qData.success) setTestQuestions(qData.questions);
        } catch (err) {
            console.error("Load Portal Error:", err);
        } finally {
            setLoadingQuestions(false);
        }
    };

    // Pre-fill phone if leadId is provided
    useEffect(() => {
        if (leadIdFromUrl) {
            setLeadId(leadIdFromUrl);
            const fetchLead = async () => {
                try {
                    const res = await fetch(`${API_BASE}/lead-details?id=${leadIdFromUrl}`);
                    const data = await res.json();
                    if (data.success) {
                        setPhone(data.phone || '');
                        setPersonalization(prev => ({
                            ...prev,
                            name: studentNameFromUrl || data.name || prev.name,
                            duration: durationFromUrl || (data.duration_for_offer ? `${data.duration_for_offer} Hours` : prev.duration)
                        }));
                        if (data.duration_for_offer) {
                            const hours = parseInt(data.duration_for_offer, 10);
                            if (!isNaN(hours) && hours > 0) {
                                setOfferTimeLeft(hours * 3600);
                            }
                        }

                        // ── Portal Gate Evaluation ──
                        // Runs AFTER lead data is fetched so we have lead.created_at
                        const statusRes = await fetch(`${API_BASE}/portal-status`);
                        const statusData = await statusRes.json();
                        const portalActive = statusData.active;
                        const bypassToken = statusData.bypassToken || '';
                        const scheduledOffAt = statusData.scheduledOffAt;
                        const portalToggleTime = statusData.updatedAt; // Time it was actually flipped

                        // 1. Check bypass token in URL
                        const urlParams = new URLSearchParams(window.location.search);
                        const urlBypass = urlParams.get('bypass');
                        const hasAdminBypass = bypassToken && urlBypass === bypassToken;

                        if (hasAdminBypass) {
                            setPortalGateChecked(true);
                            return;
                        }

                        // 2. Check for End-of-Day expiry (Universal rule)
                        // data.date_of_closure is "YYYY-MM-DD" from the link generation day
                        const todayISO = new Date().toISOString().split('T')[0];
                        const isExpired = data.date_of_closure < todayISO;

                        if (isExpired) {
                            setPortalBlocked(true);
                            setBlockReason('expired');
                            setPortalGateChecked(true);
                            return;
                        }

                        // 3. Check Portal Status / Grace Rule
                        // Grace: Link works if it was created Today (before tomorrow) 
                        // AND (Portal is ON OR created BEFORE the shutoff)
                        const leadCreatedAt = data.created_at ? new Date(data.created_at) : null;
                        
                        // Shutoff reference time: either the scheduled time or the manual toggle time
                        const shutoffPoint = scheduledOffAt ? new Date(scheduledOffAt) : (portalToggleTime ? new Date(portalToggleTime) : null);
                        
                        const isCreatedBeforeShutoff = !shutoffPoint || (leadCreatedAt && leadCreatedAt < shutoffPoint);
                        
                        // If portal is OFF, we ONLY block if it was created AFTER the shutoff
                        // (Since we already checked isExpired, we know it's a today-only link)
                        if (!portalActive && !isCreatedBeforeShutoff) {
                            setPortalBlocked(true);
                            setBlockReason('closed');
                        }
                        
                        setPortalGateChecked(true);
                    }
                } catch (err) {
                    console.warn('Could not fetch lead details', err);
                    setPortalGateChecked(true); // fail-open
                }
            };
            fetchLead();
        } else {
            // No leadId — still check portal status (e.g. direct visit)
            const checkPortalStatus = async () => {
                try {
                    const statusRes = await fetch(`${API_BASE}/portal-status`);
                    const statusData = await statusRes.json();
                    const portalActive = statusData.active;
                    const bypassToken = statusData.bypassToken || '';
                    const urlParams = new URLSearchParams(window.location.search);
                    const urlBypass = urlParams.get('bypass');
                    const hasAdminBypass = bypassToken && urlBypass === bypassToken;
                    if (!portalActive && !hasAdminBypass) {
                        setPortalBlocked(true);
                        setBlockReason('closed');
                    }
                } catch (err) {
                    console.warn('Portal status check failed (fail-open)', err);
                } finally {
                    setPortalGateChecked(true);
                }
            };
            checkPortalStatus();
        }
        loadPortalData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leadIdFromUrl, studentNameFromUrl]);

    // Assessment timer
    useEffect(() => {
        let timer;
        if (view === 'test' && timeLeft > 0) {
            timer = setInterval(() => setTimeLeft(prev => prev - 1), 1000);
        } else if (timeLeft === 0 && view === 'test') {
            // eslint-disable-next-line react-hooks/exhaustive-deps
            handleFinalSubmit();
        }
        return () => clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view, timeLeft]);

    // Offer expiry timer (for result page)
    useEffect(() => {
        let timer;
        if (view === 'offer' && offerTimeLeft > 0) {
            timer = setInterval(() => setOfferTimeLeft(prev => prev - 1), 1000);
        }
        return () => clearInterval(timer);
    }, [view, offerTimeLeft]);


    // Resend OTP timer logic
    useEffect(() => {
        let timer;
        if (resendTimer > 0) {
            timer = setInterval(() => setResendTimer(prev => prev - 1), 1000);
        }
        return () => clearInterval(timer);
    }, [resendTimer]);

    // OTP Logic via Backend
    const handleSendOtp = async () => {
        if (phone.length < 10) {
            setError('Please enter a valid 10-digit phone number');
            return;
        }
        if (resendTimer > 0) return;

        setIsLoading(true);
        setError('');
        try {
            const res = await fetch(`${API_BASE}/auth/send-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ phone, studentName: personalization.name })
            });
            const data = await res.json();
            if (!data.success) throw new Error(data.error);

            setIsOtpSent(true);
            setResendTimer(60);
        } catch (err) {
            setError(err.message || 'Failed to send OTP. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyOtp = async () => {
        if (otp.length < 4) {
            setError('Please enter the OTP');
            return;
        }
        setIsLoading(true);
        setError('');
        try {
            const res = await fetch(`${API_BASE}/auth/verify-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ phone, otp })
            });
            const data = await res.json();
            if (!data.success) throw new Error(data.error);

            // OTP verified — go straight to assessment
            setIsVerified(true);
            setView('test');
        } catch (err) {
            setError(err.message || 'Verification failed');
        } finally {
            setIsLoading(false);
        }
    };

    const handleFinalSubmit = async () => {
        if (!isVerified) {
            setError('Please verify your phone number first');
            return;
        }
        setIsSubmitting(true);
        setError('');

        try {

            // 1. Evaluate via Backend Scoring Engine
            const evalRes = await fetch(`${API_BASE}/assessment/evaluate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    leadId: leadId,
                    answers: answers,
                    questions: testQuestions
                })
            });
            const evalData = await evalRes.json();
            if (!evalData.rawScore && evalData.rawScore !== 0) throw new Error("Evaluation failed.");

            const finalResults = {
                rawScore: evalData.rawScore,
                maxScore: evalData.maxScore,
                grantAmount: evalData.grantAmount,
                grantTier: evalData.grantTier,
                tier: evalData.grantTier,
                status: evalData.status
            };

            // Identify SOP (Specifically marked question)
            const sopQuestion = testQuestions.find(q => q.is_sop);
            const sopText = answers[sopQuestion?.id] || '';

            // 2. Send all data to backend (service role saves to Supabase + syncs LSQ)
            await submitAssessment({
                leadId: leadId,
                assessmentScore: finalResults.rawScore,
                grantTier: finalResults.grantTier,
                grantAmount: finalResults.grantAmount,
                assessmentStatus: finalResults.status === 'qualified' ? "Completed" : "Not Qualified",
                assessmentCompletedAt: new Date().toISOString(),
                assessmentAnswers: JSON.stringify(answers),
                sop: sopText,
                mobile: phone,
                timeSpent: TOTAL_TIME_SECONDS - timeLeft
            });

            setResults(finalResults);
            setView('offer');
        } catch (err) {
            console.error("SUBMISSION ERROR:", err);
            setError(`Submission Failed: ${err.message || 'Unknown Error'}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Auto-save progress to backend every 3 seconds during test
    const saveToCloud = useCallback(async (currentAnswers) => {
        if (!isVerified || view !== 'test') return;
        try {
            await fetch(`${API_BASE}/assessment/autosave`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    leadId,
                    answers: currentAnswers,
                    timeSpent: TOTAL_TIME_SECONDS - timeLeft,
                    mobile: phone
                })
            });
        } catch (err) {
            console.warn('Auto-save failed (non-fatal):', err.message);
        }
    }, [isVerified, view, leadId, phone, timeLeft]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (Object.keys(answers).length > 0) saveToCloud(answers);
        }, 3000);
        return () => clearTimeout(timer);
    }, [answers, saveToCloud]);

    // Cycling evaluation messages
    useEffect(() => {
        if (isSubmitting) {
            const interval = setInterval(() => {
                setEvalStep(s => (s + 1) % 4);
            }, 1500);
            return () => clearInterval(interval);
        }
    }, [isSubmitting]);

    // --- Views (all hooks above must be declared before any early returns) ---

    // Loading gate check
    if (!portalGateChecked) {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center">
                <Loader2 className="animate-spin text-white/20" size={40} />
            </div>
        );
    }

    // Closed screen — portal is OFF and no valid bypass
    if (portalBlocked) {
        return (
            <div className="min-h-screen bg-[#000B1C] flex flex-col items-center justify-center p-6 font-sans text-center">
                <style>{`
                    @keyframes float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-12px); } }
                    .float-anim { animation: float 4s ease-in-out infinite; }
                `}</style>
                <div className="float-anim mb-10">
                    <div className="h-32 w-32 rounded-[2.5rem] bg-white/5 border border-white/10 flex items-center justify-center mx-auto">
                        <ShieldOff size={52} className="text-white/20" />
                    </div>
                </div>
                <img
                    src="https://jaro-website.s3.ap-south-1.amazonaws.com/wp-content/uploads/2025/03/10120956/Jaro-Education-Logo-417-X-74-1.png"
                    alt="Jaro Education"
                    className="h-10 object-contain mb-10 opacity-80 bg-white rounded-md px-2 py-1"
                />
                <div className="max-w-xl">
                    <span className="inline-block bg-white/5 border border-white/10 text-white/40 px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.3em] mb-8">
                        Grow with Jaro Scholarship Program
                    </span>
                    <h1 className="text-5xl lg:text-6xl font-black text-white tracking-tighter mb-6 leading-tight">
                        {blockReason === 'expired' ? (
                            <>Link Has<br /><span className="text-white/30">Expired.</span></>
                        ) : (
                            <>The Scholarship<br /><span className="text-white/30">Window Has Closed.</span></>
                        )}
                    </h1>
                    <p className="text-lg text-white/40 font-medium leading-relaxed mb-10">
                        {blockReason === 'expired' ? (
                            "Scholarship links are valid only for the day they are generated. This link has reached its end-of-day expiry limit."
                        ) : (
                            "We've received all applications for this scholarship cycle and are now reviewing submissions. We look forward to seeing you in the next one — bigger and better."
                        )}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <a
                            href="https://jaroeducation.com"
                            className="h-14 px-10 bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-2xl font-black text-sm flex items-center justify-center gap-2 transition-all"
                        >
                            Visit Jaro Education
                        </a>
                    </div>
                </div>
                <div className="mt-20 text-[10px] font-bold text-white/15 uppercase tracking-widest">
                    © 2026 Jaro Education. All Rights Reserved.
                </div>
            </div>
        );
    }

    // --- Views ---
    if (view === 'login') {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6 font-sans">
                <div className="max-w-2xl w-full bg-white rounded-[4rem] shadow-2xl overflow-hidden">
                    <div className="p-12 md:p-20 flex flex-col justify-center relative">
                        <div className="absolute top-0 left-0 w-full h-2 bg-[#1E3A8A]"></div>
                        <div className="text-center">
                            <h2 className="text-4xl font-black text-slate-900 mb-2">{isOtpSent ? "Security Code" : "Student Login"}</h2>
                            <p className="text-slate-500 font-medium mb-10">
                                {isOtpSent ? `We've sent a 6-digit code to +91 ${phone}` : "Verify your identity to begin the Grow with Jaro assessment."}
                            </p>

                            {error && (
                                <div className="mb-8 p-4 bg-red-50 border border-red-100 rounded-2xl text-red-600 text-sm font-bold flex items-center gap-3 animate-pulse">
                                    <AlertCircle size={20} /> {error}
                                </div>
                            )}
                            <div className="space-y-6">
                                {/* Instruction Set - Updated as per user request */}
                                {!isOtpSent && (
                                    <div className="bg-slate-50 border border-slate-100 rounded-3xl p-8 mb-8 text-left space-y-4">
                                        <div className="flex items-center gap-2 mb-2">
                                            <AlertCircle size={18} className="text-blue-600" />
                                            <span className="text-[12px] font-black uppercase tracking-widest text-slate-400">Assessment Guidelines</span>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                                            {[
                                                { icon: "⚠️", text: "One-time attempt only" },
                                                { icon: "⏱", text: "25 Minute timer" },
                                                { icon: "📋", text: "8 Total questions" },
                                                { icon: "🔒", text: "Do not refresh tab" },
                                                { icon: "📶", text: "Stable internet req." },
                                                { icon: "✍️", text: "No copy-paste allowed" },
                                                { icon: "🤖", text: "No AI/ChatGPT usage" },
                                                { icon: "🔍", text: "Anti-plagiarism active" }
                                            ].map((item, idx) => (
                                                <div key={idx} className="flex items-start gap-3 text-xs font-bold text-slate-600">
                                                    <span className="shrink-0">{item.icon}</span>
                                                    <span>{item.text}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {!isOtpSent ? (
                                    <div className="space-y-2 text-left">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Mobile Number</label>
                                        <div className="relative">
                                            <Phone className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-300" size={22} />
                                            <input
                                                type="tel" maxLength={10} value={phone}
                                                onChange={e => setPhone(e.target.value.replace(/\D/g, ''))}
                                                className="w-full bg-slate-50 border-2 border-slate-100 rounded-[2rem] py-6 pl-16 pr-8 font-bold text-xl outline-none focus:border-blue-600 focus:bg-white transition-all tracking-[0.2em]"
                                                placeholder="00000 00000"
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-2 text-left animate-in slide-in-from-bottom-4">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">OTP CODE</label>
                                        <div className="relative">
                                            <Lock className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-300" size={22} />
                                            <input
                                                type="text" maxLength={6} value={otp}
                                                onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
                                                className="w-full bg-slate-50 border-2 border-slate-100 rounded-[2rem] py-6 pl-16 pr-8 font-black text-3xl outline-none focus:border-blue-600 focus:bg-white transition-all tracking-[0.5em]"
                                                placeholder="000000"
                                            />
                                        </div>
                                        <p className="text-xs text-slate-400 mt-4 leading-relaxed font-medium">
                                            Didn't receive it? {resendTimer > 0 ? `Resend in ${resendTimer}s` : <button onClick={handleSendOtp} className="text-blue-600 font-bold underline">Resend Code</button>}
                                        </p>
                                    </div>
                                )}

                                <button
                                    onClick={isOtpSent ? handleVerifyOtp : handleSendOtp}
                                    disabled={isLoading || (isOtpSent ? otp.length < 4 : (phone.length < 10 || resendTimer > 0))}
                                    className="w-full h-20 bg-[#1E3A8A] text-white rounded-[2rem] font-black text-xl hover:bg-blue-800 transition-all flex items-center justify-center gap-3 shadow-2xl disabled:opacity-30"
                                >
                                    {isLoading ? <Loader2 className="animate-spin" /> : (isOtpSent ? "Verify & Start" : "Get OTP")}
                                </button>
                                <p className="text-center text-[10px] text-slate-400 font-bold uppercase tracking-widest">Do Not Use AI to Answer the Questions</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }


    // --- Views ---
    if (view === 'landing') {
        return (
            <div className="bg-slate-50 text-slate-900 overflow-x-hidden font-sans scroll-smooth">
                <style>{`
                    @keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
                    .marquee-container { display: flex; width: max-content; animation: marquee 40s linear infinite; }
                    .glass-header { background: rgba(255, 255, 255, 0.9); backdrop-filter: blur(12px); }
                    .kamyabi-text { color: #FFB01D; font-style: italic; }
                    .viksit-gradient { background: linear-gradient(to bottom, #fdfbf7 0%, #f3e9d9 100%); }
                    .pulse-btn { animation: pulse-glow 2s infinite; }
                    @keyframes pulse-glow { 0% { box-shadow: 0 0 0 0 rgba(255, 176, 29, 0.4); } 70% { box-shadow: 0 0 0 15px rgba(255, 176, 29, 0); } 100% { box-shadow: 0 0 0 0 rgba(255, 176, 29, 0); } }
                `}</style>

                {/* Header */}
                <header className="h-20 border-b border-slate-100 sticky top-0 z-50 glass-header px-6">
                    <div className="max-w-7xl mx-auto h-full flex justify-between items-center">
                        <div className="flex items-center gap-10">
                            <img src="https://jaro-website.s3.ap-south-1.amazonaws.com/wp-content/uploads/2025/03/10120956/Jaro-Education-Logo-417-X-74-1.png" alt="Jaro Education" className="h-7 sm:h-9 object-contain" />
                            <nav className="hidden lg:flex gap-8 text-[13px] font-bold text-slate-500 uppercase tracking-wider">
                                <a href="#partners" className="hover:text-blue-900">Our Associations</a>
                                <a href="#csr" className="hover:text-blue-900">CSR</a>
                                <a href="#specializations" className="hover:text-blue-900">Specializations</a>
                            </nav>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="hidden md:flex bg-[#FFE5E5] text-[#E11A20] font-black text-sm px-5 py-2.5 rounded-full border border-[#E11A20]">
                                ⏳ {formatHeaderTime(headerTimer)}
                            </div>
                            <button onClick={() => setView('login')} className="bg-[#1E3A8A] text-white rounded-xl px-7 h-11 text-[11px] font-black uppercase tracking-widest shadow-lg">Start Test Now</button>
                        </div>
                    </div>
                </header>

                {/* Hero */}
                <section className="bg-[#000B1C] text-white px-6 min-h-[90vh] flex items-center">
                    <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center w-full py-20">
                        <div>
                            <span className="inline-block bg-[#FFF6D8] text-[#855B00] border border-[#EBD79B] px-3 py-1 rounded-md text-[10px] font-black uppercase tracking-wider mb-6">#GrowWithJaro 2026</span>
                            <h1 className="text-4xl sm:text-5xl lg:text-[72px] font-black tracking-tighter mb-4 leading-[0.95]">
                                <span className="text-[#FFB01D]">Hey {personalization.name || 'Student'},</span><br />
                                it's time to <span className="kamyabi-text">Grow with Jaro.</span>
                            </h1>
                            <p className="text-xl text-slate-400 mb-8 max-w-xl italic">"Turn your ambition into action with an exclusive scholarship up to Rs.15,000 for your online mba journey"</p>
                            <div className="flex flex-wrap gap-4">
                                <button onClick={() => setView('login')} className="pulse-btn h-16 px-10 bg-[#FFB01D] text-slate-950 rounded-2xl text-lg font-black flex items-center gap-3 shadow-xl">
                                    Start Test Now <ArrowRight />
                                </button>
                                <div className="bg-white/5 border border-white/10 p-3 rounded-2xl flex items-center gap-3">
                                    <div className="flex -space-x-2">
                                        {partners.slice(0, 3).map((p, i) => (
                                            <div key={i} className="w-9 h-9 rounded-full bg-white p-1 border-2 border-[#000B1C]"><img src={p.logo} alt="" className="w-full h-full object-contain" /></div>
                                        ))}
                                    </div>
                                    <div className="text-[9px] font-black uppercase text-slate-400 leading-tight">350K+ Professionals<br /><span className="text-white">Success Stories</span></div>
                                </div>
                            </div>
                        </div>
                        <div className="bg-[#051630] rounded-[3rem] p-4 border border-white/5 shadow-2xl relative aspect-video">
                            <video autoPlay muted loop playsInline className="w-full h-full object-cover rounded-[2rem]">
                                <source src="https://jaro-website.s3.ap-south-1.amazonaws.com/wp-content/uploads/2025/10/29115704/vIKRANT.mp4" type="video/mp4" />
                            </video>
                        </div>
                    </div>
                </section>

                {/* Marquee */}
                <section id="partners" className="py-20 border-y border-slate-100 bg-white">
                    <h3 className="text-center text-3xl font-black mb-12 text-[#1E3A8A]">Our <span className="text-[#000B1C]">Associations</span></h3>
                    <div className="relative flex overflow-hidden">
                        <div className="marquee-container">
                            {[...partners, ...partners].map((p, i) => {
                                return (
                                    <div key={i} className="mx-4 px-8 py-6 border border-slate-100 rounded-3xl bg-white flex flex-col items-center justify-center min-w-[240px] h-60 shadow-sm transition-all hover:scale-105">
                                        <img
                                            src={p.logo}
                                            alt={p.name}
                                            className="h-36 max-h-[70%] object-contain mb-4"
                                        />
                                        <p className="text-[11px] font-black uppercase text-slate-600 text-center leading-tight tracking-wider">
                                            {p.name}
                                        </p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* Viksit Bharat */}
                <section id="csr" className="min-h-[70vh] flex items-center viksit-gradient py-20 px-6">
                    <div className="max-w-7xl mx-auto bg-white rounded-[4rem] shadow-2xl overflow-hidden grid md:grid-cols-2">
                        <div className="h-full"><img src="https://jaro-website.s3.ap-south-1.amazonaws.com/wp-content/uploads/2025/03/Modi-Sanjay-sir-photo-532x320-1.jpg" alt="Viksit Bharat" className="w-full h-full object-cover" /></div>
                        <div className="p-12 md:p-20 text-center flex flex-col justify-center items-center">
                            <p className="text-slate-500 font-medium mb-1">Democratising Quality Education for a</p>
                            <h2 className="text-5xl font-black text-[#1E3A8A] mb-8 uppercase">VIKSIT BHARAT</h2>
                            <p className="text-slate-600 font-medium leading-relaxed max-w-md italic">"A powerful moment as Hon'ble PM Narendra Modi and our CMD Dr. Sanjay Salunkhe reflect on grooming young talent for the 2047 vision."</p>
                            <button onClick={() => setView('login')} className="mt-10 h-14 px-10 bg-[#1E3A8A] text-white rounded-2xl font-black">Start Now</button>
                        </div>
                    </div>
                </section>

                {/* Steps */}
                <section className="py-32 px-6 max-w-7xl mx-auto">
                    <div className="text-center mb-20">
                        <h2 className="text-5xl font-black text-[#1E3A8A] mb-4">How to Avail Scholarship</h2>
                        <p className="text-slate-500 font-medium text-lg">Your streamlined path to professional funding.</p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            { step: "01", title: "Register Profile", desc: "Establish eligibility by linking your unique secure profile." },
                            { step: "02", title: "Take Quick Test", desc: "Complete a 15-minute aptitude assessment to determine your tier." },
                            { step: "03", title: "Unlock Scholarship", desc: "Receive your career acceleration scholarship" }
                        ].map((s, i) => (
                            <div key={i} className="p-12 rounded-[3.5rem] bg-white border border-slate-100 shadow-sm relative overflow-hidden group hover:shadow-xl transition-all">
                                <div className="text-8xl font-black text-[#FFCF24] absolute -top-4 -right-4 opacity-10 group-hover:opacity-30 transition-all">{s.step}</div>
                                <h4 className="text-2xl font-black text-slate-900 mb-4">{s.title}</h4>
                                <p className="text-slate-500 font-medium leading-relaxed">{s.desc}</p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Tiers */}
                <section className="py-24 px-6 bg-white">
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center mb-16">
                            <h2 className="text-5xl font-black text-[#1E3A8A] mb-4">Scholarship Opportunities</h2>
                            <p className="text-slate-500 font-medium text-lg">Unlock your Career Acceleration Scholarship based on your profile, intent, and application timing.</p>
                        </div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
                            <div className="relative p-10 rounded-[3rem] bg-white border-2 border-[#FFB01D] shadow-xl scale-105">
                                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#FFB01D] text-slate-950 text-[10px] font-black px-4 py-1 rounded-full uppercase whitespace-nowrap">Highest Value | Limited Slots</div>
                                <Trophy className="text-[#FFB01D] mb-6 w-10 h-10" />
                                <h3 className="text-2xl font-black mb-2">Elite Scholarship</h3>
                                <div className="text-3xl font-black text-[#1E3A8A] mb-4">₹15,000</div>
                                <p className="text-slate-500 text-sm font-medium leading-relaxed text-left">Awarded to top-performing candidates with exceptional profiles, strong intent, and early application timing. Reserved for a limited number of high-potential applicants.</p>
                            </div>

                            {[
                                { title: "Merit Scholarship", amt: "₹10,000", desc: "For candidates with strong professional backgrounds and clearly defined career goals, demonstrating serious commitment to growth." },
                                { title: "Priority Scholarship", amt: "₹7,500", desc: "For early applicants who take timely action toward enrollment and show readiness to move forward." },
                                { title: "Base Scholarship", amt: "₹5,000", desc: "Offered to eligible candidates who complete the assessment and meet the minimum selection criteria." }
                            ].map((t, i) => (
                                <div key={i} className="p-10 rounded-[3rem] bg-white border border-slate-100 hover:shadow-xl transition-all">
                                    <Award className="text-slate-400 mb-6 w-10 h-10" /><h3 className="text-2xl font-black mb-2">{t.title}</h3>
                                    <div className="text-3xl font-black text-[#1E3A8A] mb-4">{t.amt}</div>
                                    <p className="text-slate-500 text-sm font-medium leading-relaxed text-left">{t.desc}</p>
                                </div>
                            ))}
                        </div>
                        <div className="text-center">
                            <p className="text-slate-400 text-xs font-bold italic">“Final scholarship amounts are determined based on the selected program, candidate profile, and assessment performance.”</p>
                        </div>
                    </div>
                </section>

                {/* Mission Video Section */}
                <section className="py-32 px-6 max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-20 items-center">
                        <div>
                            <span className="bg-blue-50 text-blue-900 px-4 py-1 rounded-full text-[10px] font-black uppercase mb-6 inline-block">Our Mission</span>
                            <h2 className="text-5xl font-black tracking-tighter mb-8 leading-tight">Transforming Careers<br /><span className="text-blue-600">Through Innovation.</span></h2>
                            <p className="text-slate-500 text-lg mb-10 font-medium leading-relaxed">For over 16 years, Jaro Education has been transforming careers by democratising access to high-quality education for working professionals.</p>
                            <div className="space-y-6">
                                <div className="flex gap-4 items-start">
                                    <Check className="text-green-500 mt-1 shrink-0" />
                                    <p className="text-[13px] text-slate-700 leading-relaxed tracking-wide">As a trusted enabler, Jaro Education collaborates with premier institutions such as IITs, IIMs, and top NIRF-ranked universities to deliver industry-relevant online and executive education.</p>
                                </div>
                                <div className="flex gap-4 items-start">
                                    <Check className="text-green-500 mt-1 shrink-0" />
                                    <p className="text-[13px] text-slate-700 leading-relaxed tracking-wide">This ensures learners gain not just knowledge, but skills that translate into meaningful career growth and long-term advancement.</p>
                                </div>
                            </div>
                        </div>
                        <div className="bg-slate-900 rounded-[3rem] overflow-hidden shadow-2xl border-8 border-slate-100">
                            <a href="https://www.youtube.com/watch?v=xOVAvlubfgw" target="_blank" rel="noreferrer" className="relative group block">
                                <img src="https://img.youtube.com/vi/xOVAvlubfgw/hqdefault.jpg" className="w-full" alt="Video" />
                                <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition-all">
                                    <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center text-white shadow-2xl group-hover:scale-110 transition-transform"><Zap fill="white" /></div>
                                </div>
                            </a>
                        </div>
                    </div>
                </section>

                {/* The Jaro Advantage Section */}
                <section className="py-24 px-6 bg-[#000B1C] text-white rounded-[4rem] mx-4 mb-16 shadow-2xl relative overflow-hidden">
                    <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center w-full">
                        {/* Left Side: Stats */}
                        <div className="text-left">
                            <span className="inline-block bg-[#1E3A8A] text-white px-4 py-1 rounded-full text-[10px] font-black uppercase tracking-widest mb-4">
                                The Jaro Advantage.
                            </span>
                            <h2 className="text-[3.2rem] font-black mb-6 leading-tight tracking-tighter">
                                Transforming Ambition into Real Career Outcomes
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
                                <div className="p-5 rounded-3xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all">
                                    <p className="text-xl font-black text-[#FFB01D] mb-1">3.5 Lakh+</p>
                                    <p className="text-[10px] text-slate-400 tracking-wider leading-relaxed">Careers Transformed — From Executives to C-Suite Leaders & Entrepreneurs</p>
                                </div>
                                <div className="p-5 rounded-3xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all">
                                    <p className="text-xl font-black text-[#FFB01D] mb-1">#1 Leader</p>
                                    <p className="text-[10px] text-slate-400 tracking-wider leading-relaxed">EdTech of the Year (2025) Recognized by ET Now – West Edition for empowering India’s professionals</p>
                                </div>
                                <div className="p-5 rounded-3xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all">
                                    <p className="text-xl font-black text-[#FFB01D] mb-1">Publicly Listed</p>
                                    <p className="text-[10px] text-slate-400 tracking-wider leading-relaxed">Trusted & Recognised on NSE & BSE</p>
                                </div>
                                <div className="p-5 rounded-3xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all">
                                    <p className="text-xl font-black text-[#FFB01D] mb-1">Premier Programs</p>
                                    <p className="text-[10px] text-slate-400 tracking-wider leading-relaxed">In Collaboration with IITs, IIMs & Top NIRF-Ranked Universities</p>
                                </div>
                                <div className="p-5 rounded-3xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all col-span-1 md:col-span-2">
                                    <p className="text-xl font-black text-[#FFB01D] mb-1">Lifelong Learning Support</p>
                                    <p className="text-[10px] text-slate-400 tracking-wider leading-relaxed">Access to 1000+ Free Learning Resources for Continuous Growth</p>
                                </div>
                            </div>
                        </div>

                        {/* Right Side: Promo Card */}
                        <div className="p-10 bg-[#1E3A8A] rounded-[3rem] text-white text-center shadow-2xl relative overflow-hidden">
                            <div className="absolute top-0 right-0 p-6 opacity-5 rotate-12">
                                <Trophy className="w-40 h-40" />
                            </div>
                            <span className="inline-block bg-[#FFB01D] text-slate-950 px-5 py-2 rounded-full text-xs font-black uppercase mb-6">
                                LIMITED TIME!
                            </span>
                            <p className="text-[#FFB01D] font-black text-xs uppercase tracking-[0.3em] mb-3">
                                Maximum Scholarship Reward
                            </p>
                            <h1 className="text-6xl font-black mb-4 tracking-tighter italic text-[#FFB01D]">
                                Rs.15,000
                            </h1>
                            <p className="text-blue-100 font-bold mb-8 leading-relaxed text-base italic opacity-90">
                                100 Deserving candidates can also get a 100% scholarship.
                            </p>
                            <button onClick={() => setView('login')}
                                className="w-full h-16 bg-white text-blue-900 rounded-2xl font-black text-lg hover:bg-amber-400 transition-all active:scale-95 shadow-xl">
                                Claim Scholarship Now
                            </button>
                        </div>
                    </div>
                </section>

                {/* Salary Section */}
                <section className="py-32 px-6 bg-white">
                    <div className="max-w-7xl mx-auto bg-[#1E3A8A] text-white rounded-[4rem] p-12 md:p-20 overflow-hidden relative">
                        <div className="grid lg:grid-cols-2 gap-20 relative z-10">
                            <div className="text-left">
                                <h3 className="text-4xl font-black mb-10 leading-tight">Maximum Salary Growth Projections After MBA</h3>
                                <div className="space-y-6">
                                    {[
                                        { role: "Management Consultant", sal: "₹70,00,000" },
                                        { role: "Finance Manager", sal: "₹50,00,000" },
                                        { role: "Marketing Manager", sal: "₹40,00,000" },
                                        { role: "Operations Manager", sal: "₹35,00,000" },
                                        { role: "HR Manager", sal: "₹32,20,000" },
                                        { role: "Business Analyst", sal: "₹30,00,000" }
                                    ].map((r, i) => (
                                        <div key={i} className="flex flex-col md:flex-row justify-between items-start md:items-center py-4 border-b border-white/10 gap-2 text-left">
                                            <span className="font-bold text-sm md:text-base">{r.role}</span>
                                            <span className="font-black text-[#FFB01D] text-xs md:text-sm">{r.sal}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-6">
                                {[
                                    { icon: <PieChart />, text: "Strategic Thinking" },
                                    { icon: <Cpu />, text: "Technical Skills" },
                                    { icon: <Target />, text: "Decision Making" },
                                    { icon: <Briefcase />, text: "Project Mgmt" }
                                ].map((sk, i) => (
                                    <div key={i} className="p-8 bg-white/5 border border-white/10 rounded-3xl flex flex-col items-center justify-center text-center gap-4">
                                        <div className="text-[#FFB01D]">{sk.icon}</div>
                                        <p className="text-[10px] font-black uppercase tracking-widest">{sk.text}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="absolute top-0 right-0 p-20 opacity-5 rotate-12"><Landmark size={200} /></div>
                    </div>
                </section>

                {/* Testimonials */}
                <section className="py-32 bg-slate-950 text-white overflow-hidden">
                    <h2 className="text-center text-4xl font-black mb-20">Scholarship Recipients</h2>
                    <div className="flex gap-8 overflow-x-auto px-10 scrollbar-hide">
                        {[
                            { name: "Rohan Sharma", role: "Marketing Manager to Sr. Strategist", grant: "Elite Scholarship", quote: "I was delaying my MBA due to cost. The scholarship made the decision a no-brainer." },
                            { name: "Priya Patel", role: "Software Engineer to Product Manager", grant: "Merit Scholarship", quote: "The assessment was quick and challenged my business acumen. Claiming it was easy." },
                            { name: "Amit Desai", role: "Sales Executive to Regional Head", grant: "Priority Scholarship", quote: "Jaro guided me to the best University for my profile. The scholarship was an instant win." }
                        ].map((t, i) => (
                            <div key={i} className="min-w-[450px] p-12 rounded-[4rem] bg-white/5 border border-white/10 relative">
                                <Quote className="text-white/5 absolute top-10 right-10 w-24 h-24" />
                                <div className="flex gap-1 text-amber-400 mb-8"><Star fill="currentColor" size={14} /><Star fill="currentColor" size={14} /><Star fill="currentColor" size={14} /><Star fill="currentColor" size={14} /><Star fill="currentColor" size={14} /></div>
                                <p className="text-xl italic text-slate-300 mb-10 leading-relaxed">"{t.quote}"</p>
                                <div className="pt-8 border-t border-white/10">
                                    <p className="text-lg font-black text-left">{t.name}</p>
                                    <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3 text-left">{t.role}</p>
                                    <span className="bg-amber-100 text-amber-900 px-3 py-1 rounded-full text-[9px] font-black uppercase text-left">Winner</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Influencers */}
                <section className="py-32 text-blue">
                    <div className="max-w-7xl mx-auto px-6 text-center mb-16">
                        <h2 className="text-5xl font-black mb-4">Influencers Love Us ❤️</h2>
                        <p className="text-slate-400">Join the community of professionals who trust Jaro Education.</p>
                    </div>
                    <div className="flex gap-8 overflow-x-auto px-10 pb-10 scrollbar-hide">
                        {[
                            { name: "Sharan Hegde", followers: "2.9M", img: "https://jaro-website.s3.ap-south-1.amazonaws.com/wp-content/uploads/2025/03/571691032_18087617180503403_5284247510221328608_n.jpg" },
                            { name: "CA Rachna Ranade", followers: "1.2M", img: "https://jaro-website.s3.ap-south-1.amazonaws.com/wp-content/uploads/2025/03/568352047_18529065778001748_8773757528623600004_n-1-.jpg" },
                            { name: "Riya Upreti", followers: "966K", img: "https://jaro-website.s3.ap-south-1.amazonaws.com/wp-content/uploads/2025/03/Riya.png" }
                        ].map((inf, i) => (
                            <div key={i} className="min-w-[400px] p-6 rounded-[3rem] bg-blur/5 border border-blue/10">
                                <div className="h-[30rem] rounded-2xl overflow-hidden mb-6 relative">
                                    <img src={inf.img} alt={inf.name} className="w-full h-full object-cover" />
                                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center cursor-pointer">▶</div>
                                </div>
                                <p className="text-xl font-black text-center mb-1">{inf.name} <BadgeCheck className="inline text-blue-400 w-5 h-5" /></p>
                                <p className="text-sm text-slate-400 text-center uppercase tracking-widest">{inf.followers} Followers</p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Footer */}
                <footer className="bg-[#000B1C] text-white pt-32 pb-24 px-6 rounded-t-[5rem]">
                    <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-20 items-center">
                        <div className="text-left">
                            <h2 className="text-4xl font-black mb-4 uppercase text-[#1E3A8A]">Career Counselling<br /><span className="text-white">You Can Trust</span></h2>
                            <p className="text-xl text-slate-400 mb-12 italic">"Jaro Education is a publicly listed organisation with 16+ years experience."</p>
                            <button onClick={() => setView('login')} className="h-16 px-12 bg-white text-[#000B1C] rounded-2xl font-black text-lg">Start Test Now</button>
                        </div>
                        <div className="bg-white p-16 rounded-[4rem] text-center shadow-2xl">
                            <img src="https://jaro-website.s3.ap-south-1.amazonaws.com/wp-content/uploads/2025/03/10120956/Jaro-Education-Logo-417-X-74-1.png" alt="Jaro" className="h-16 mx-auto mb-8 object-contain" />
                            <div className="h-px bg-slate-100 my-8"></div>
                            <h3 className="text-3xl md:text-5xl font-black text-blue-900 italic tracking-tighter leading-tight">Officially listed in BSE & NSE</h3>
                            <div className="mt-6 bg-slate-50 py-3 rounded-2xl border border-slate-100 font-bold text-slate-500 uppercase text-[10px]">Since 2009</div>
                        </div>
                    </div>
                </footer>

                {/* Popup */}
                {showPopup && (
                    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[1000] p-6">
                        <div className="bg-white rounded-[3rem] p-10 max-w-md w-full text-center shadow-2xl relative">
                            <button onClick={() => setShowPopup(false)} className="absolute top-6 right-6 text-slate-400 hover:text-black">✕</button>
                            <h2 className="text-3xl font-black text-[#1E3A8A] mb-4">🎓 Unlock Your Scholarship</h2>
                            <p className="text-slate-500 mb-8 font-medium">Don't miss your chance to get up to <b>₹15,000 scholarship</b>.</p>
                            <button onClick={() => { setShowPopup(false); setView('login'); }} className="w-full h-14 bg-[#FFB01D] text-black font-black rounded-2xl">Start Test Now</button>
                        </div>
                    </div>
                )}
            </div>
        );
    }


    // Assessment View
    if (view === 'test') {
        if (loadingQuestions) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" size={48} /></div>;

        if (isSubmitting) {
            const steps = [
                { msg: 'Analyzing Profile Response...', icon: <User className="animate-pulse" /> },
                { msg: 'Calculating Merit Score...', icon: <Target className="animate-bounce" /> },
                { msg: 'Verifying Timing Velocity...', icon: <Clock className="animate-spin" /> },
                { msg: 'Finalizing Grant Offer...', icon: <Sparkles className="animate-pulse" /> }
            ];

            return (
                <div className="min-h-screen bg-[#000B1C] flex items-center justify-center p-6 font-sans">
                    <div className="text-center space-y-12 max-w-lg w-full">
                        <div className="relative">
                            <div className="absolute inset-0 bg-blue-500/20 blur-[120px] rounded-full"></div>
                            <div className="relative h-32 w-32 bg-indigo-600 rounded-[3rem] mx-auto flex items-center justify-center text-white shadow-2xl shadow-indigo-600/50">
                                {steps[evalStep].icon}
                            </div>
                        </div>
                        <div className="space-y-4">
                            <h2 className="text-3xl font-black text-white tracking-tight">{steps[evalStep].msg}</h2>
                            <p className="text-slate-500 font-bold uppercase tracking-[0.3em] text-[10px]">Deep Intelligence Evaluation Active</p>
                        </div>
                        <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-indigo-500 transition-all duration-1000 ease-out"
                                style={{ width: `${(evalStep + 1) * 25}%` }}
                            ></div>
                        </div>
                    </div>
                </div>
            );
        }

        const q = testQuestions[currentQ];
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col">
                <nav className="bg-white border-b px-8 py-5 flex justify-between items-center sticky top-0 z-50">
                    <span className="font-black text-slate-900 tracking-tighter uppercase text-xs">GROW WITH JARO LIVE ENGINE</span>
                    {personalization.name && (
                        <div className="text-xs text-indigo-600 font-bold">
                            Welcome, {personalization.name}!
                        </div>
                    )}
                    <div className={`px-6 py-2 rounded-full font-black tabular-nums flex items-center gap-2 transition-colors ${timeLeft < 300 ? 'bg-red-50 text-red-600 animate-pulse' : 'bg-slate-100 text-slate-700'}`}>
                        <Clock size={16} /> {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
                    </div>
                </nav>
                <main className="max-w-4xl mx-auto w-full p-6 md:py-12 text-left">
                    <div className="bg-white p-10 md:p-16 rounded-[3rem] shadow-xl border border-slate-100">
                        <span className="px-3 py-1 bg-slate-100 text-slate-500 text-[10px] font-black uppercase tracking-widest rounded-md mb-8 inline-block">Step {currentQ + 1} / {testQuestions.length}</span>
                        <h2 className="text-3xl font-black text-slate-900 mb-12 leading-tight">{q.text}</h2>
                        <div className="min-h-[300px]">
                            {q.type === 'subjective' ? (
                                <textarea
                                    className="w-full h-64 p-8 bg-slate-50 border-2 border-slate-100 rounded-3xl outline-none focus:border-indigo-500 focus:bg-white transition-all text-lg font-medium"
                                    placeholder="Type your response..."
                                    value={answers[q.id] || ''}
                                    onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                                />
                            ) : (
                                <div className="grid gap-4">
                                    {Object.entries(q.options || {}).map(([key, val]) => (
                                        <button key={key} onClick={() => setAnswers({ ...answers, [q.id]: key })} className={`p-6 rounded-2xl border-2 text-left font-bold flex items-center gap-4 transition-all ${answers[q.id] === key ? 'border-indigo-600 bg-indigo-50 text-indigo-900' : 'border-slate-100 hover:border-slate-200 text-slate-600'}`}>
                                            <span className={`h-10 w-10 rounded-lg flex items-center justify-center font-black ${answers[q.id] === key ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-400'}`}>{key.toUpperCase()}</span>
                                            {val}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="mt-12 pt-8 border-t flex gap-4">
                            <button disabled={currentQ === 0} onClick={() => setCurrentQ(v => v - 1)} className="px-8 py-4 bg-slate-100 rounded-2xl font-black text-slate-500 disabled:opacity-30">Back</button>
                            {currentQ === testQuestions.length - 1 ? (
                                <button onClick={handleFinalSubmit} disabled={isSubmitting || !answers[q.id] || (q.is_sop && answers[q.id].length < 100)} className="flex-1 bg-slate-900 text-white py-4 rounded-2xl font-black text-xl hover:bg-indigo-600 transition-all flex items-center justify-center gap-2 shadow-lg disabled:opacity-50">
                                    {isSubmitting ? <Loader2 className="animate-spin" size={24} /> : "Generate Grant Offer"}
                                </button>
                            ) : (
                                <button onClick={() => setCurrentQ(v => v + 1)} disabled={!answers[q.id]} className="flex-1 bg-slate-900 text-white py-4 rounded-2xl font-black text-xl hover:bg-indigo-600 transition-all disabled:opacity-50">Continue</button>
                            )}
                        </div>
                    </div>

                </main>
            </div>
        );
    }

    // Offer (Success/Failure) View
    if (view === 'offer' && results) {
        const isQualified = results.status !== 'not_qualified';

        if (isQualified) {
            return (
                <div className="min-h-screen bg-[#F8FAFC] flex flex-col font-sans">
                    <Confetti width={width} height={height} />

                    {/* Offer Validity Top Bar */}
                    <div className="bg-[#1E3A8A] text-white py-3 px-6 flex justify-center items-center gap-3 sticky top-0 z-[100] shadow-lg">
                        <Timer size={18} className="animate-pulse text-[#FFB01D]" />
                        <span className="text-[11px] md:text-sm font-bold tracking-wide uppercase">
                            Offer Expires In: <span className="text-[#FFB01D] font-black">{formatTime(offerTimeLeft)}</span>
                        </span>
                    </div>

                    <main className="flex-1 p-6 md:p-12 max-w-2xl mx-auto w-full">
                        <div className="bg-white rounded-[3rem] shadow-2xl overflow-hidden border border-slate-100 relative mb-8">
                            {/* Success Header */}
                            <div className="p-10 text-center bg-gradient-to-br from-[#000B1C] to-[#1E3A8A] text-white">
                                <div className="w-20 h-20 bg-[#FFB01D] rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl shadow-amber-500/20">
                                    <Trophy size={40} className="text-[#000B1C]" />
                                </div>
                                <h1 className="text-4xl font-black mb-2 tracking-tight">Congratulations!</h1>
                                <p className="text-blue-100 opacity-80 italic font-medium">Excellent performance, {personalization.name}.</p>
                            </div>

                            <div className="p-8 space-y-8">
                                {/* Scholarship Secured Card */}
                                <div className="bg-amber-50 rounded-[2.5rem] p-8 border-2 border-amber-200 text-center relative overflow-hidden group">
                                    <Sparkles className="absolute top-4 right-4 text-amber-300 opacity-50" />
                                    <p className="text-[11px] font-black uppercase text-amber-700 tracking-[0.2em] mb-3">Scholarship Reward Secured</p>
                                    <h2 className="text-5xl font-black text-[#000B1C] mb-2 tracking-tighter italic">Rs.{results.grantAmount.toLocaleString()}</h2>
                                    <p className="text-xl font-bold text-amber-800 tracking-tight">{results.tier}</p>
                                </div>

                                {/* Terms Section */}
                                <div className="bg-blue-50/50 rounded-3xl p-6 border border-blue-100">
                                    <div className="flex items-center gap-3 mb-4">
                                        <Info size={18} className="text-[#1E3A8A]" />
                                        <p className="text-xs font-black uppercase text-[#1E3A8A] tracking-wider">How to avail this reward:</p>
                                    </div>
                                    <ul className="space-y-3">
                                        <li className="flex items-start gap-3">
                                            <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center shrink-0 mt-0.5"><Check size={12} className="text-blue-600" /></div>
                                            <p className="text-xs font-semibold text-slate-600 leading-relaxed"> Admission formalities must be completed within the Offer Validity window.</p>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center shrink-0 mt-0.5"><Check size={12} className="text-blue-600" /></div>
                                            <p className="text-xs font-semibold text-slate-600 leading-relaxed">Scholarship amount will be credited to the student's account within the stipulated timeline.</p>
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            {/* Footer Action with Application Completion Message */}
                            <div className="p-8 pt-0">
                                {isCtaClicked ? (
                                    <div className="bg-[#000B1C] text-white p-6 rounded-[2rem] text-center animate-in zoom-in-95 duration-300">
                                        <p className="text-lg font-black mb-1 italic text-[#FFB01D]">Action Required</p>
                                        <p className="text-sm font-medium">Please complete your application with your Career Counselor to secure the reward.</p>
                                    </div>
                                ) : (
                                    <button onClick={() => setIsCtaClicked(true)} className="w-full h-18 bg-[#1E3A8A] text-white rounded-[2rem] font-black text-xl shadow-2xl flex items-center justify-center gap-3">
                                        Claim & Secure Reward <ChevronRight />
                                    </button>
                                )}
                            </div>
                        </div>
                    </main>
                </div>
            );
        } else {
            return (
                <div className="min-h-screen bg-[#F8FAFC] flex flex-col font-sans">
                    <main className="flex-1 p-6 md:p-12 max-w-2xl mx-auto w-full flex items-center justify-center">
                        <div className="bg-white rounded-[3rem] shadow-2xl overflow-hidden border border-slate-100 w-full">
                            {/* Failure Header */}
                            <div className="p-10 text-center bg-slate-100 text-slate-800">
                                <div className="w-20 h-20 bg-slate-200 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <Target size={40} className="text-slate-400" />
                                </div>
                                <h1 className="text-3xl font-black mb-2 tracking-tight">Better luck next time!</h1>
                                <p className="text-slate-500 font-medium">Keep striving for your career goals, {personalization.name}.</p>
                            </div>

                            <div className="p-10 space-y-6 text-center">
                                <p className="text-slate-600 font-medium leading-relaxed">
                                    While your current assessment does not meet the qualification criteria for this scholarship cycle, we encourage you to continue upskilling and exploring our premier programs.
                                </p>

                                {/* Simplified Action */}
                                <button
                                    onClick={() => window.location.reload()}
                                    className="w-full h-18 bg-slate-800 text-white rounded-[2rem] font-black text-xl shadow-lg hover:bg-slate-900 transition-all flex items-center justify-center gap-3"
                                >
                                    Back to Home <ChevronRight />
                                </button>
                            </div>

                            <div className="p-6 bg-slate-50 border-t border-slate-100 text-center">
                                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                                    Reference ID: <span className="text-slate-600">{Math.random().toString(36).substr(2, 9).toUpperCase()}</span>
                                </p>
                            </div>
                        </div>
                    </main>
                </div>
            );
        }
    }

    return null;
}


